import { Check, X, Brain } from "lucide-react";

import { Button } from "@/components/ui/button";
import { addMemoryItem } from "@/lib/planningMemory";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

// Renders AI-suggested planning-memory items. Nothing is saved unless the user
// approves each item. Fully user-controlled (spec §18-20).
export default function MemorySuggestionCard({ batch, onResolved }) {
  const { t } = useTranslation();
  if (!batch || !batch.suggestions?.length) return null;

  const catLabel = (c) => t(`memory.categories.${c}`);

  const approve = (s, idx) => {
    addMemoryItem(s.category, s.text);
    toast.success(t("memory.added"));
    onResolved?.(idx);
  };

  return (
    <div
      className="rounded-[20px] border border-border bg-card p-4 shadow-sm"
      data-testid="memory-suggestion-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Brain className="h-4 w-4 text-slate-500" />
        <p className="text-sm font-semibold text-slate-900">{t("memory.suggestTitle")}</p>
      </div>
      <p className="text-xs text-slate-500 mb-3">{t("memory.suggestBody")}</p>
      <div className="space-y-2">
        {batch.suggestions.map((s, idx) => (
          <div
            key={`${s.category}:${s.text}`}
            className="flex items-start gap-2 rounded-xl border border-border bg-white px-3 py-2.5"
            data-testid={`memory-suggestion-item-${idx}`}
          >
            <div className="min-w-0 flex-1">
              <span className="block text-[10px] uppercase tracking-[0.16em] text-slate-500">
                {catLabel(s.category)}
              </span>
              <span className="block text-sm text-slate-800 mt-0.5">{s.text}</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                className="h-8 rounded-full px-3 gap-1"
                onClick={() => approve(s, idx)}
                data-testid={`memory-approve-${idx}`}
              >
                <Check className="h-3.5 w-3.5" />
                {t("memory.approve")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 rounded-full p-0 text-slate-500"
                onClick={() => onResolved?.(idx)}
                data-testid={`memory-dismiss-${idx}`}
                aria-label={t("memory.dismiss")}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
